import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, AlertTriangle, ArrowRight, ShieldCheck, Phone, Mail, MapPin, Sparkles, CheckCircle, Calendar, Award, ShieldAlert } from 'lucide-react';
import Gallery from '../components/Gallery';
import ContactForm from '../components/ContactForm';
import ReviewSlider from '../components/ReviewSlider';
import SEO from '../components/SEO';
import { getOptimizedImageUrl } from '../lib/seo';

const caseDescriptions = {
  'normal': 'I 2019 var BM Maler med til at opføre NORMALS nye 3.600 kvadratmeter store domicil på Godthåbsvej i Skanderborg. Her en lille håndfuld år senere drøner samarbejdet fortsat derud af, og de dygtige malersvende kigger jævnligt forbi og stryger malerpenslen hen over de pletter og skrammer, der uundgåeligt opstår i et stort kontordomicil med knap 200 ansatte.',
  'wine-mage': 'Vejle har fået et nyt vinunivers i My Garage, hvor Wine Mage har slået dørene op til unikt, stilfuldt og rustikt showroom, der aldrig helt stopper med at overraske såvel øjne som smagsløg.',
  'formula': 'Det oser af hestekræfter, passion og eksklusivitet, når du træder ind ad døren hos Formula i My Garage i Vejle. I showroomet finder du håndplukkede biler fra Ferrari og Maserati. To af verdens mest elskede og eftertragtede bilmærker.'
};

// Animation presets
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05
    }
  }
};

export default function PageDetail() {
  const { slug } = useParams();
  const location = useLocation();
  
  // Prepend 'ydelser-' if we are on a service subpage
  const isServicePage = location.pathname.startsWith('/ydelser/');
  const refSlug = isServicePage ? `ydelser-${slug}` : (slug || 'index');

  const [page, setPage] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all pages from CMS
        const pagesRes = await fetch('https://api.dev.auxo.dk/public/content/pages');
        if (!pagesRes.ok) throw new Error('Kunne ikke hente sideindhold.');
        const pages = await pagesRes.json();

        // Find the page matching our ref_slug
        const currentPage = pages.find(p => p.ref_slug === refSlug);

        if (!currentPage) {
          setPage(null);
          setLoading(false);
          return;
        }

        setPage(currentPage);

        // If this is the kundecases page, filter and set cases
        if (refSlug === 'kundecases') {
          const matchingCases = pages.filter(p => p.parent_slug === 'kundecases');
          setCases(matchingCases);
        } else {
          setCases([]);
        }

        // Update SEO Meta Tags
        document.title = currentPage.meta_title || `${currentPage.title} | BM Maler A/S`;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
          metaDesc.setAttribute('content', currentPage.meta_description || '');
        }

        // Fetch matching gallery if gallery_slug is present
        if (currentPage.gallery_slug) {
          const galleriesRes = await fetch('https://api.dev.auxo.dk/public/content/galleries');
          if (galleriesRes.ok) {
            const galleries = await galleriesRes.json();
            const matchingGallery = galleries.find(g => g.ref_slug === currentPage.gallery_slug);
            setGallery(matchingGallery);
          }
        } else {
          setGallery(null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, [refSlug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 bg-slate-950 text-white">
        <Loader2 className="w-10 h-10 animate-spin text-[#F1B528]" />
        <p className="text-slate-400 text-sm font-semibold">Henter indhold...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-slate-950 text-white px-4">
        <div className="max-w-xl mx-auto text-center space-y-6">
          <div className="inline-flex p-4 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-black tracking-tight">Der skete en fejl</h1>
          <p className="text-slate-400 text-sm leading-relaxed">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#F1B528] hover:bg-[#D99C1B] text-slate-950 px-6 py-3 rounded-full text-sm font-bold transition-all duration-300"
          >
            Gå til forsiden
          </Link>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <>
        <SEO 
          title="404 - Siden blev ikke fundet | BM Maler A/S"
          description="Beklager, den side du leder efter findes ikke eller er flyttet."
          noindex={true}
        />
        <div className="min-h-[80vh] flex items-center justify-center bg-slate-950 text-white px-4">
        <div className="max-w-xl mx-auto text-center space-y-6">
          <div className="inline-flex p-4 rounded-full bg-[#F1B528]/10 text-[#F1B528] border border-[#F1B528]/20">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-black tracking-tight">Siden blev ikke fundet</h1>
          <p className="text-slate-400 text-sm leading-relaxed">
            Beklager, men den side du leder efter findes ikke eller er blevet flyttet.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#F1B528] hover:bg-[#D99C1B] text-slate-950 px-6 py-3 rounded-full text-sm font-bold transition-all duration-300"
          >
            Gå til forsiden
          </Link>
        </div>
      </div>
    </>
  );
}

  // Determine top banner image
  const topBanner = (refSlug === 'profil' || page.slug === 'profil')
    ? 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/image.png'
    : (page.images && page.images[0]
      ? page.images[0]
      : 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/f074c4a2-e267-415d-b35e-cc61789452c9.jpg');

  // Helper to format page content with premium quote styling
  const formatPageContent = (content) => {
    if (!content) return '';

    let formatted = content;

    // Replace phone numbers and email addresses (existing functionality)
    formatted = formatted
      .replace(/75\s*69\s*59\s*00/g, '<a href="tel:+4575695900" class="text-[#F1B528] hover:text-[#D99C1B] transition-colors font-bold whitespace-nowrap">75 69 59 00</a>')
      .replace(/bm@bmmaler\.dk/g, '<a href="mailto:bm@bmmaler.dk" class="text-[#F1B528] hover:text-[#D99C1B] transition-colors font-bold">bm@bmmaler.dk</a>');

    // Regex to match the quote structure:
    // 1. Heading (h2 or h3)
    // 2. Quote paragraph starting with a quotation mark
    // 3. Author paragraph starting with a dash
    const quoteRegex = /<(h[23])>([^<]+)<\/\1>\s*<p>\s*[“"”„'’](.*?)[”"““'’]\s*<\/p>\s*<p>\s*[–—-]\s*(.*?)\s*<\/p>/g;

    formatted = formatted.replace(quoteRegex, (match, tag, heading, quoteText, author) => {
      return `
        <div class="my-12 bg-slate-50 border-l-4 border-[#F1B528] rounded-r-3xl p-6 sm:p-8 md:p-10 shadow-sm relative overflow-hidden">
          <span class="block text-xs font-bold uppercase tracking-widest text-[#D99C1B] mb-3">${heading}</span>
          
          <blockquote class="text-slate-800 text-base sm:text-lg md:text-xl font-medium italic leading-relaxed relative z-10">
            “${quoteText}”
          </blockquote>
          
          <cite class="block text-slate-500 text-xs sm:text-sm font-semibold mt-4 not-italic flex items-center gap-2">
            <span class="w-4 h-px bg-slate-300"></span>
            ${author}
          </cite>
        </div>
      `;
    });

    return formatted;
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="space-y-16 sm:space-y-24 pb-24 bg-white"
    >
      <SEO 
        title={page.meta_title || page.title} 
        description={page.meta_description} 
        image={topBanner} 
      />
      {/* Premium Editorial Hero */}
      <section className="relative min-h-[45vh] flex items-center justify-center overflow-hidden bg-slate-950 text-white py-20">
        {/* Background Overlay & Grids */}
        <div className="absolute inset-0 z-0">
          <motion.img
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.5 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            src={getOptimizedImageUrl(topBanner, { width: 1400, quality: 80 })}
            alt={`${page.title} - BM Maler A/S professionelt malerarbejde`}
            width="1400"
            height="500"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-transparent" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <motion.div 
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-[#F1B528]/10 border border-[#F1B528]/20 px-3.5 py-1.5 rounded-full text-[#F1B528] text-xs font-bold uppercase tracking-wider"
          >
            <Sparkles className="w-4 h-4" />
            BM Maler A/S
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-none max-w-4xl mx-auto text-white"
          >
            {page.title}
          </motion.h1>

          {/* Breadcrumbs */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="flex items-center justify-center gap-2 text-sm font-bold text-slate-400"
          >
            <Link to="/" className="hover:text-[#F1B528] transition-colors">Forside</Link>
            <span>/</span>
            {isServicePage ? (
              <>
                <span className="text-slate-500">Ydelser</span>
                <span>/</span>
              </>
            ) : null}
            <span className="text-white">{page.title}</span>
          </motion.div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column - Verbatim Text Content */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-8 space-y-12"
          >
            <article
              className="rich-text-content max-w-none"
              dangerouslySetInnerHTML={{ 
                __html: formatPageContent(page.content)
              }}
            />

            {/* Embed Contact Form on the Contact Page */}
            {refSlug === 'kontakt' && (
              <div className="w-full bg-transparent sm:bg-slate-900 p-0 sm:p-12 border-0 sm:border border-slate-800 shadow-none sm:shadow-2xl rounded-none sm:rounded-3xl">
                <ContactForm />
              </div>
            )}

            {/* Embed Gallery if present */}
            {gallery && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="pt-12 border-t border-slate-100"
              >
                <Gallery images={gallery.images} title={gallery.title || "Glimt af vores arbejde"} />
              </motion.div>
            )}

            {/* If this is the profil page, render the team profile section featuring Lene & Mike */}
            {refSlug === 'profil' && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="pt-12 border-t border-slate-100 space-y-10"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Mike Jensen Card */}
                  <div className="bg-slate-50 border border-slate-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col group">
                    <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                      <img 
                        src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/11680-bm-maler-mike.jpg", { width: 600, quality: 80 })} 
                        alt="Mike Jensen - Malermester og Partner hos BM Maler" 
                        width="600"
                        height="450"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
                    </div>
                    <div className="p-6 flex flex-col space-y-1">
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#D99C1B] transition-colors">
                        Mike Jensen
                      </h3>
                    </div>
                  </div>

                  {/* Lene Møller Thisvad Card */}
                  <div className="bg-slate-50 border border-slate-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col group">
                    <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                      <img 
                        src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/11680-bm-maler-lene.jpg", { width: 600, quality: 80 })} 
                        alt="Lene Møller Thisvad - Maler og Partner hos BM Maler" 
                        width="600"
                        height="450"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
                    </div>
                    <div className="p-6 flex flex-col space-y-1">
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#D99C1B] transition-colors">
                        Lene Møller Thisvad
                      </h3>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* If this is the kundecases page, render the cases grid */}
            {refSlug === 'kundecases' && cases.length > 0 && (
              <div className="pt-12 border-t border-slate-100 space-y-12">
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                  Vores udvalgte cases
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {cases.map((c) => {
                    const desc = caseDescriptions[c.ref_slug] || '';
                    const caseImg = c.images && c.images[0] ? c.images[0] : '';
                    return (
                      <div 
                        key={c.id}
                        className="group bg-slate-50 border border-slate-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full"
                      >
                        {caseImg && (
                          <div className="relative aspect-video overflow-hidden bg-slate-100">
                            <img 
                              src={getOptimizedImageUrl(caseImg, { width: 600, quality: 80 })} 
                              alt={`BM Maler case - ${c.title}`}
                              width="600"
                              height="338"
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}
                        <div className="p-6 sm:p-8 flex flex-col flex-grow justify-between space-y-6">
                          <div className="space-y-3">
                            <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#D99C1B] transition-colors">
                              {c.title}
                            </h3>
                            <p className="text-slate-600 text-sm leading-relaxed">
                              {desc}
                            </p>
                          </div>
                          <div>
                            <Link 
                              to={`/${c.ref_slug}`}
                              className="inline-flex items-center gap-2 text-sm font-bold text-slate-950 hover:text-[#D99C1B] transition-colors group/btn"
                            >
                              Læs hele casen
                              <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>

          {/* Right Column - Sidebar Info */}
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="lg:col-span-4 space-y-8 lg:sticky lg:top-28"
          >
            
            {/* BM Maler Quality Badge */}
            <motion.div 
              variants={fadeInUp}
              className="bg-slate-950 text-white p-8 rounded-3xl space-y-6 border border-slate-800 shadow-xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#F1B528]/5 rounded-full blur-2xl pointer-events-none" />
              
              <div className="inline-flex p-3 rounded-full bg-[#F1B528]/10 text-[#F1B528]">
                <ShieldCheck className="w-5 h-5" />
              </div>
              
              <div className="space-y-2">
                <h3 className="text-xl font-bold tracking-tight">Kvalitetsgaranti</h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Vi er stolt medlem af Danske Malermestres Garantiordning. Det sikrer dig professionelt udført malerarbejde i højeste kvalitet, uanset opgavens omfang.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-800/80 flex items-center gap-4">
                <img
                  src="https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/69cdfa93-d212-46d0-9d1c-ba4195739c50.png"
                  alt="Danske Malermestre"
                  className="h-12 w-auto object-contain"
                />
                <div className="text-xs text-slate-400">
                  <span className="block font-bold text-white">Danske Malermestre</span>
                  <span>Garantiordning op til 150.000 kr.</span>
                </div>
              </div>
            </motion.div>

            {/* Quick Facts Sidebar */}
            <motion.div 
              variants={fadeInUp}
              className="bg-slate-50 border border-slate-100 p-8 rounded-3xl space-y-6"
            >
              <h3 className="text-slate-950 font-bold text-lg tracking-tight">Hvorfor vælge os?</h3>
              
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D99C1B] shrink-0 mt-0.5" />
                  <span className="text-xs text-slate-600 font-medium">Uddannede og faglærte malere</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D99C1B] shrink-0 mt-0.5" />
                  <span className="text-xs text-slate-600 font-medium">Knap 70 års erfaring (siden 1957)</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D99C1B] shrink-0 mt-0.5" />
                  <span className="text-xs text-slate-600 font-medium">Vi overholder altid tidsplaner</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D99C1B] shrink-0 mt-0.5" />
                  <span className="text-xs text-slate-600 font-medium">Grundig afdækning og oprydning</span>
                </li>
              </ul>
            </motion.div>

            {/* Quick Contact Sidebar */}
            <motion.div 
              variants={fadeInUp}
              className="bg-slate-50 border border-slate-100 p-8 rounded-3xl space-y-6"
            >
              <h3 className="text-slate-950 font-bold text-lg tracking-tight">Har du spørgsmål?</h3>
              <ul className="space-y-5 text-xs">
                <li className="flex items-start gap-3.5">
                  <div className="p-2 rounded-full bg-[#F1B528]/10 text-[#D99C1B] shrink-0">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="block text-slate-400 text-[10px] font-bold uppercase tracking-wider">Ring direkte</span>
                    <a href="tel:+4575695900" className="text-[#F1B528] hover:text-[#D99C1B] transition-colors font-bold whitespace-nowrap">75 69 59 00</a>
                  </div>
                </li>
                <li className="flex items-start gap-3.5">
                  <div className="p-2 rounded-full bg-[#F1B528]/10 text-[#D99C1B] shrink-0">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="block text-slate-400 text-[10px] font-bold uppercase tracking-wider">Send en e-mail</span>
                    <a href="mailto:bm@bmmaler.dk" className="text-[#F1B528] hover:text-[#D99C1B] transition-colors font-bold whitespace-nowrap">bm@bmmaler.dk</a>
                  </div>
                </li>
                <li className="flex items-start gap-3.5">
                  <div className="p-2 rounded-full bg-[#F1B528]/10 text-[#D99C1B] shrink-0">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="block text-slate-400 text-[10px] font-bold uppercase tracking-wider">Vores adresse</span>
                    <span className="font-bold text-slate-950">Håndværkervej 3A, 7120 Vejle Øst</span>
                  </div>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Embed ReviewSlider on the Kundecases Page */}
      {refSlug === 'kundecases' && (
        <motion.section 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-slate-950 py-16 sm:py-24 text-white"
        >
          <ReviewSlider />
        </motion.section>
      )}
    </motion.div>
  );
}
